/**
 * One page of the rescue's saved foster homes, searched, ordered and counted by the server.
 *
 * The counterpart to `useFosterHomes`, which fetches every home. Both are legitimate: the
 * roster table renders a page, while the assign-to-foster picker offers all of them and the
 * placements table resolves each row's home out of the whole list. Two hooks rather than one
 * shared list, since a page cannot answer either of those.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  rescueFosterHomesPagedQuery,
  type RescueFosterHomeSortKey,
  type RescueFosterHomesPagedData,
} from '../lib/rescueQueries';
import { adaptFosterHomes, type FosterHome } from '../lib/fosterAdapters';
import type { SortDir } from '../lib/types';
import useStaleCut from './useStaleCut';

/** Columns the roster table can be sorted by (clicking their header). */
export type RosterSortKey = 'foster' | 'status' | 'placements';

/** The page's vocabulary for a column against the schema's. */
const SORT_KEY_TO_GQL: Record<RosterSortKey, RescueFosterHomeSortKey> = {
  foster: 'FOSTER',
  status: 'STATUS',
  placements: 'PLACEMENTS',
};

export interface UsePagedFosterHomesResult {
  homes: FosterHome[];
  /** Every home matching the query, not just the page. */
  totalCount: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: boolean;
  goToPage: (page: number) => void;
  refetch: () => void;
}

export default function usePagedFosterHomes(
  pageSize: number,
  searchQuery: string,
  sort: { key: RosterSortKey; dir: SortDir } | null,
  options?: { skip?: boolean },
): UsePagedFosterHomesResult {
  const [page, setPage] = useState(1);

  // A search or a re-order makes the page the user is on a slice of a different list, so start
  // again at the top rather than leave them mid-way through a list they never saw the start of.
  //
  // Adjusted during render rather than in an effect, and read through a local the rest of this
  // render uses. An effect lands a render later — after a request has gone out at the old
  // offset under the new ordering, which can answer last and paint exactly the mid-list slice
  // this reset exists to avoid.
  const signature = `${searchQuery}|${sort ? `${sort.key}:${sort.dir}` : 'none'}`;
  const [prevSignature, setPrevSignature] = useState(signature);
  let currentPage = page;
  if (prevSignature !== signature) {
    setPrevSignature(signature);
    setPage(1);
    currentPage = 1;
  }

  const variables = useMemo(() => {
    const input: Record<string, unknown> = { limit: pageSize, offset: (currentPage - 1) * pageSize };
    // Absent rather than an empty string for a blank search: the server treats any present
    // `query` as a filter to apply.
    const trimmed = searchQuery.trim();
    if (trimmed) {
      input.query = trimmed;
    }
    // Omitted rather than nulled when no column is active, which asks for the server's own
    // default ordering — newest home first.
    if (sort) {
      input.sortKey = SORT_KEY_TO_GQL[sort.key];
      input.sortDirection = sort.dir === 'desc' ? 'DESC' : 'ASC';
    }
    return { input };
  }, [pageSize, currentPage, searchQuery, sort]);

  const { data, previousData, loading, error, refetch } = useQuery<RescueFosterHomesPagedData>(
    rescueFosterHomesPagedQuery,
    {
      variables,
      skip: options?.skip,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );

  // Keep the last successful page on screen while the next one is in flight, so re-sorting
  // dims the table rather than emptying it — but not when the query failed for a cut it has
  // never answered, where those rows belong to the ordering before this one. See useStaleCut.
  const stale = useStaleCut(JSON.stringify(variables), !!data);
  const result = stale && error ? undefined : (data ?? previousData)?.rescueFosterHomesPaged;
  const totalCount = result?.totalCount ?? 0;

  const homes = useMemo(() => adaptFosterHomes(result?.items ?? []), [result?.items]);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Archiving the last home on the last page shrinks the roster under the user: the count
  // drops, the page they are on stops existing, and nothing on screen says so — the table's
  // empty state is keyed on the total, which is not zero, and the pager hides itself once
  // there is only one page. That leaves a blank table with no control to get back, and the
  // page lives above this hook, so it outlasts a tab switch.
  //
  // Only once a result is in hand: an initial or skipped load reports zero for want of an
  // answer, which is not the same as a roster that shrank.
  if (result && currentPage > totalPages) {
    setPage(totalPages);
    currentPage = totalPages;
  }

  const goToPage = useCallback((target: number) => {
    setPage(Math.max(1, target));
  }, []);

  return {
    homes,
    totalCount,
    currentPage,
    totalPages,
    loading,
    error: !!error,
    goToPage,
    // Wrapped: callers wire this straight to onClick, and Apollo merges any truthy
    // argument into `variables` — a React event would land in the request payload.
    refetch: () => {
      void refetch();
    },
  };
}
